import {CommentsCategory, useAddComment} from '@domain/comments'
import {DataContentWrapper} from '@hconnect/common/containers'
import {useUpmPlant} from '@hconnect/upm'
import {useCallback, useMemo, useRef, useState} from 'react'
import {SubmitHandler} from 'react-hook-form'
import {useTranslation} from 'react-i18next'

import {EditTaskCard} from '../../components'
import {OilAnalysisActionForm} from '../../components/form'
import {TaskResolvedMandatoryComment} from '../../components/TaskDetails/TaskResolvedMandatoryComment'
import {OilAnalysisCommentsQueryKeyPrefix, FEATURE_NAMESPACE} from '../../consts'
import {useOilAnalysisAction, useUpdateOilAnalysisAction} from '../../hooks'
import {mapToOilAnalysisAction, mapToOilAnalysisActionFormValues} from '../../mappers'
import {
  HProduceTask,
  TaskSource,
  HProduceTaskIdentifiers,
  OilAnalysisActionFormValues,
  SideCardItemMode,
  SideCardModes,
  TaskStatus
} from '../../types'

type Props = {
  task: HProduceTaskIdentifiers
  onClose: (original?: HProduceTask, editVersion?: HProduceTask) => void
  onTaskTypeChange: (taskType: TaskSource | undefined) => void
  setMode: (next: SideCardModes) => void
}

export const EditOilAnalysisActionContainer = ({
  task,
  onClose,
  onTaskTypeChange,
  setMode
}: Props) => {
  const {t} = useTranslation(FEATURE_NAMESPACE)
  const upmPlant = useUpmPlant({plantId: task.plantId, shouldRaiseError: false})
  const plantUpmId = upmPlant?.upmId
  const {data, isLoading, isError, refetch, isRefetching} = useOilAnalysisAction({
    plantUpmId,
    externalId: task.externalId
  })
  const plantTimezone = upmPlant?.timezone
  const {mutateAsync: updateOilAnalysisAction} = useUpdateOilAnalysisAction()
  const {mutateAsync: addComment} = useAddComment(
    {
      plantId: task.plantId,
      commentsCategory: CommentsCategory.OilAnalysisActions
    },
    OilAnalysisCommentsQueryKeyPrefix
  )

  const [isCommentDialogOpen, setIsCommentDialogOpen] = useState(false)
  const [statusComment, setStatusComment] = useState<string | null>(null)
  const resetStatusRef = useRef<(() => void) | null>(null)

  const handleCancel = () => {
    setMode({mode: SideCardItemMode.Details, itemId: task.id})
  }

  const handleStatusChange = useCallback(
    (newValue: TaskStatus | null, resetStatus: () => void) => {
      if (!data) return
      const isChangingToDone = data.status !== newValue && newValue === TaskStatus.Done
      if (isChangingToDone) {
        resetStatusRef.current = resetStatus
        setIsCommentDialogOpen(true)
      } else {
        setStatusComment(null)
      }
    },
    [data]
  )

  const handleCommentDialogClose = useCallback(() => {
    setIsCommentDialogOpen(false)
    resetStatusRef.current?.()
    resetStatusRef.current = null
  }, [])

  const handleCommentSubmit = useCallback((comment: string) => {
    setStatusComment(comment)
    setIsCommentDialogOpen(false)
    resetStatusRef.current = null
  }, [])

  const handleSubmit: SubmitHandler<OilAnalysisActionFormValues> = async (formValues) => {
    if (!formValues || !plantUpmId || !data) {
      return
    }

    const payload = mapToOilAnalysisAction(formValues, task.externalId, plantUpmId)

    const isStatusChangingToDone =
      data.status !== formValues.status && formValues.status === TaskStatus.Done

    await updateOilAnalysisAction(payload, {
      onSuccess: async (updatedOilAnalysisAction) => {
        if (isStatusChangingToDone && statusComment) {
          await addComment({
            value: statusComment,
            commentCategoryInstanceId: updatedOilAnalysisAction.id
          })
        }
        setMode({mode: SideCardItemMode.Details, itemId: task.id})
      }
    })
  }

  const initialValues = useMemo(() => {
    return data && plantTimezone ? mapToOilAnalysisActionFormValues(data, plantTimezone) : undefined
  }, [data, plantTimezone])

  return (
    <>
      <DataContentWrapper
        data={initialValues}
        showLoader={isLoading}
        isError={isError}
        retryFunction={refetch}
        isRefetching={isRefetching}
        noDataSettings={{
          noDataMessage: t('task.message.taskNotFound'),
          onActionClick: () => onClose()
        }}
      >
        {data && plantTimezone && (
          <EditTaskCard title={data.title}>
            <OilAnalysisActionForm
              externalTaskId={task.externalId}
              plantId={upmPlant.plantId}
              onSubmit={handleSubmit}
              onCancel={handleCancel}
              onTaskTypeChange={onTaskTypeChange}
              initialValues={initialValues}
              timezone={plantTimezone}
              onStatusChange={handleStatusChange}
            />
          </EditTaskCard>
        )}
      </DataContentWrapper>
      <TaskResolvedMandatoryComment
        data-test-id="task-resolved-mandatory-comment-modal"
        open={isCommentDialogOpen}
        onClose={handleCommentDialogClose}
        onSubmit={handleCommentSubmit}
      />
    </>
  )
}
